import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { Summary } from '../../types/PostProduction';

interface SummarySectionProps {
  summary: Summary;
  editMode: boolean;
  onChange: (field: string, value: any) => void;
}

const SummarySection: React.FC<SummarySectionProps> = ({
  summary,
  editMode,
  onChange,
}) => {
  // Challenge types from proposal
  const challengeTypes = [
    'Technical difficulties',
    'Low attendance',
    'Weather related',
    'Staffing issues',
    'Artist/performer issues',
    'Timing/scheduling',
    'Budget/financial',
    'None',
    'Other'
  ];

  // Learning categories from proposal
  const learningCategories = [
    'Pricing strategy',
    'Marketing approach',
    'Technical preparation',
    'Scheduling',
    'Artist selection',
    'Audience engagement',
    'Operational process',
    'None',
    'Other'
  ];

  // Follow-up action categories from proposal
  const followUpActions = [
    'Contact artist/agent',
    'Equipment purchase/rental',
    'Venue maintenance',
    'Process improvement',
    'Marketing adjustment',
    'Financial follow-up',
    'None',
    'Other'
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Event Summary & Assessment
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Overall Success Rating</Typography>
            <Rating
              name="success-rating"
              value={summary.success_rating}
              precision={0.5}
              onChange={(e, value) => onChange('success_rating', value || 0)}
              disabled={!editMode}
            />
          </Box>

          <TextField
            label="Event Highlights"
            multiline
            rows={4}
            fullWidth
            value={summary.highlights}
            onChange={(e) => onChange('highlights', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for what went well"
          />

          {/* NEW: Challenge Types (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Challenge Types</InputLabel>
            <Select
              multiple
              value={summary.challengeTypes || []}
              onChange={(e) => onChange('challengeTypes', e.target.value)}
              input={<OutlinedInput label="Challenge Types" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {challengeTypes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Challenges Free Text (Backwards Compatible) */}
          <TextField
            label="Challenge Details"
            multiline
            rows={4}
            fullWidth
            value={summary.challenges}
            onChange={(e) => onChange('challenges', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific details"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* NEW: Learning Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Learning Categories</InputLabel>
            <Select
              multiple
              value={summary.learningCategories || []}
              onChange={(e) => onChange('learningCategories', e.target.value)}
              input={<OutlinedInput label="Learning Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {learningCategories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Lessons Learned Free Text (Backwards Compatible) */}
          <TextField
            label="Lessons Learned Details"
            multiline
            rows={4}
            fullWidth
            value={summary.lessons_learned}
            onChange={(e) => onChange('lessons_learned', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific learnings"
          />

          {/* NEW: Follow-up Action Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Action Required</InputLabel>
            <Select
              multiple
              value={summary.followUpActionCategories || []}
              onChange={(e) => onChange('followUpActionCategories', e.target.value)}
              input={<OutlinedInput label="Action Required" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {followUpActions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EXISTING: Follow-up Actions Free Text (Backwards Compatible) */}
          <TextField
            label="Follow-up Action Details"
            multiline
            rows={4}
            fullWidth
            value={summary.follow_up_actions}
            onChange={(e) => onChange('follow_up_actions', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific actions"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SummarySection;